import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { Route, Redirect, Switch } from 'react-router-dom';
import App from 'Containers/App';
import Homepage from 'Screens/Homepage';
import ExploreModelsPage from 'Screens/ExploreModelsPage';
import ExploreTopicsPage from 'Screens/ExploreTopicsPage';
import SchoolsPage from 'Screens/SchoolsPage';
import ClassesPage from 'Screens/ClassesPage';
import ClassPage from 'Screens/ClassPage';
import PlayerPage from 'Screens/PlayerPage';
import LoginPage from 'Screens/LoginPage';
import LogoutPage from 'Screens/LogoutPage';
import InvitesPage from 'Screens/InvitesPage';
import UsersPage from 'Screens/UsersPage';
import UserEdit from 'Screens/UserEdit';
import FreeTrial from 'Screens/FreeTrial';
import FreeTrialOnlineLearning from 'Screens/FreeTrialOnlineLearning';
import ErrorPage from 'Screens/ErrorPage';
import ForgotPassword from 'Screens/ForgotPassword';
import ResetPassword from 'Screens/ResetPassword';
import PrivacyPolicy from 'Screens/PrivacyPolicy';
import TermsOfService from 'Screens/TermsOfService';
import EmailUnsubscribe from 'Screens/EmailUnsubscribe';
import PricingPage from 'Screens/PricingPage';
import LoggedInPaymentPage from 'Screens/LoggedInPaymentPage';
import ExpiredPaymentPage from 'Screens/ExpiredPaymentPage';
import PaymentConfirmation from 'Screens/PaymentConfirmation';
import LoggedOutPayment from 'Containers/LoggedOutPayment/LoggedOutPayment';
import PaymentUsernameCheck from 'Screens/PaymentUsernameCheck';
import PaymentLogin from 'Screens/PaymentLogin';
import CreateAccount from 'Screens/CreateAccount';
import NewAccountPaymentPage from 'Screens/NewAccountPaymentPage';

import AdminStatsPage from 'Screens/AdminStatsPage';
import AdminPrettyJson from 'Screens/AdminPrettyJson';
import AdminPageV2 from 'Screens/AdminPageV2';
import EducatorsStatsPage from 'Screens/AdminPageV2/EducatorsStatsPage';
import SchoolsStatsPage from 'Screens/AdminPageV2/SchoolsStatsPage';
import GraphsStatsPage from 'Screens/AdminPageV2/GraphsStatsPage';
import ExpiredTrialErrorPage from 'Screens/ExpiredTrialErrorPage';
import { hasSpecialAccess } from 'Utils/specialAccess';
import IsraelDemoWrapper from 'Containers/IsraelDemoWrapper';
import UserSettings from 'Screens/UserSettings';

import {
  userIsAuthenticated,
  userIsLoggingOut,
  userIsEducator,
  userIsAdmin,
  userIsSuperAdmin,
} from './auth';

import { ROUTES } from 'Settings/route-strings';
import PaymentMethodSettings from './screens/UserSettings/PaymentMethodSettings';

const REDIRECTS = [
  { from: '/terms', to: ROUTES.TERMS_OF_SERVICE },
  { from: '/login', to: ROUTES.SIGN_IN },
  { from: '/logout', to: ROUTES.SIGN_OUT },
  { from: '/models', to: ROUTES.EXPLORE, exact: true },
  { from: '/apps', to: ROUTES.EXPLORE, exact: true },
  { from: '/models/:gameSlug', to: `${ROUTES.EXPLORE}/:gameSlug` },
  { from: '/apps/:gameSlug', to: `${ROUTES.EXPLORE}/:gameSlug` },
  { from: '/teachers', to: ROUTES.USERS },
  { from: '/settings/school', to: ROUTES.SETTINGS_SUBSCRIPTION, exact: true },
  { from: ROUTES.SUBSCRIPTION, to: ROUTES.SUBSCRIPTION_USERNAME, exact: true },
];

// The function used to log pages as people move through the site.
// todo move to utils folder
const logPageView = () => {
  ReactGA.set({ page: window.location.pathname });
  ReactGA.pageview(window.location.pathname);
};

const RouteWrapper = (props) => {
  useEffect(() => {
    logPageView();
  });
  return <Route {...props} />;
};

const Routes = () => (
  <App>
    <Switch>
      {/* ================= REDIRECTS  ================= */}
      {REDIRECTS.map((redirectProps) => (
        <Redirect key={redirectProps.to} {...redirectProps} />
      ))}
      {/* ================= INDEX  ================= */}
      <RouteWrapper exact path={ROUTES.INDEX} component={Homepage} />
      {/* ============= STATIC ============= */}
      <RouteWrapper path={ROUTES.PRIVACY_POLICY} component={PrivacyPolicy} />
      <RouteWrapper path={ROUTES.TERMS_OF_SERVICE} component={TermsOfService} />
      <RouteWrapper path={ROUTES.PRICING} component={PricingPage} />
      {/* ============= PAYMENT ============= */}
      <RouteWrapper
        path={`${ROUTES.FREE_TRIAL}/:source?`}
        component={FreeTrial}
      />
      {/* Special route for free access due to school shutdowns during the Coronavirus outbreak */}
      <RouteWrapper
        path={`${ROUTES.FREE_TRIAL_ONLINE_LEARNING}`}
        component={FreeTrialOnlineLearning}
      />
      <RouteWrapper path={ROUTES.SIGN_IN} component={LoginPage} />
      <RouteWrapper
        path={ROUTES.SIGN_OUT}
        component={userIsLoggingOut(LogoutPage)}
      />
      <RouteWrapper
        exact
        path={ROUTES.RESET_PASSWORD}
        component={ForgotPassword}
      />
      <RouteWrapper
        path={`${ROUTES.RESET_PASSWORD}/:resetPasswordToken`}
        component={ResetPassword}
      />
      {/* ============= MODELS/APPS ============= */}
      <RouteWrapper
        path={ROUTES.EXPLORE}
        component={(props) => {
          const { location } = props;
          const Component = ({ authToken, setAuthToken }) => {
            /* NOTE: For the israel demo, special access has been granted
            to users who visit the site with ?ref=israel 
            */
            return (
              <Switch>
                <RouteWrapper
                  exact
                  path={ROUTES.EXPLORE_TOPICS}
                  component={ExploreTopicsPage}
                />
                <RouteWrapper
                  path={`${ROUTES.EXPLORE}/:gameSlug/:topicSlug`}
                  component={PlayerPage}
                />
                <RouteWrapper
                  path={`${ROUTES.EXPLORE}/:gameSlug`}
                  component={PlayerPage}
                />
                <RouteWrapper
                  path={ROUTES.EXPLORE}
                  component={ExploreModelsPage}
                />
              </Switch>
            );
          };

          const ResultComponent = hasSpecialAccess(location)
            ? () => (
                <IsraelDemoWrapper>
                  <Component />
                </IsraelDemoWrapper>
              )
            : userIsAuthenticated(Component);
          return <ResultComponent {...props} />;
        }}
      />
      {/* ============= USER GROUPS ============= */}
      <RouteWrapper
        path={ROUTES.SCHOOLS}
        component={userIsSuperAdmin(SchoolsPage)}
      />
      <RouteWrapper
        exact
        path={ROUTES.CLASSES}
        component={userIsAuthenticated(ClassesPage)}
      />
      <RouteWrapper
        path={`${ROUTES.CLASSES}/:classId`}
        component={userIsAuthenticated(ClassPage)}
      />
      {/* ============= USERS ============= */}
      <RouteWrapper
        exact
        path={ROUTES.USERS}
        component={userIsEducator(UsersPage)}
      />
      <RouteWrapper
        path={`${ROUTES.USERS}/:id`}
        component={userIsAuthenticated(UserEdit)}
      />
      <RouteWrapper
        path={`${ROUTES.INVITES}/:inviteToken`}
        component={InvitesPage}
      />
      {/* ============= MISC SETTINGS ============= */}
      <RouteWrapper
        exact
        path={ROUTES.SETTINGS_EMAIL_UNSUBSCRIBE}
        component={EmailUnsubscribe}
        title="Email Subscriptions"
      />
      <RouteWrapper
        exact
        path={ROUTES.SETTINGS_PAYMENT}
        component={userIsAdmin(PaymentMethodSettings)}
        title="Add Payment Method"
      />
      <RouteWrapper
        exact
        path={ROUTES.SETTINGS_SUBSCRIPTION_PAYMENT}
        component={userIsAdmin(LoggedInPaymentPage)}
      />
      <RouteWrapper
        exact
        path={ROUTES.SETTINGS_SUBSCRIPTION_ACTIVATION}
        component={ExpiredPaymentPage}
      />
      <RouteWrapper
        exact
        path={ROUTES.SETTINGS_SUBSCRIPTION_CONFIRMATION}
        component={userIsAdmin(PaymentConfirmation)}
      />
      {/* ============= USER SETTINGS ============= */}
      <RouteWrapper
        path={ROUTES.SETTINGS}
        component={userIsEducator(UserSettings)}
      />
      {/* ============= EXPIRED TRIAL ============= */}
      <RouteWrapper
        path={ROUTES.ERROR_EXPIRED_TRIAL}
        component={ExpiredTrialErrorPage}
      />
      {/* ============= LOGGED OUT PAYMENT ============= */}
      <RouteWrapper
        path={ROUTES.SUBSCRIPTION}
        component={() => (
          <LoggedOutPayment>
            <Switch>
              <RouteWrapper
                path={ROUTES.SUBSCRIPTION_USERNAME}
                component={PaymentUsernameCheck}
              />
              <RouteWrapper
                path={ROUTES.SUBSCRIPTION_LOGIN}
                component={PaymentLogin}
              />
              <RouteWrapper
                path={ROUTES.SUBSCRIPTION_CREATE_ACCOUNT}
                component={CreateAccount}
              />
              <RouteWrapper
                path={ROUTES.SUBSCRIPTION_PAYMENT}
                component={NewAccountPaymentPage}
              />
              <RouteWrapper
                component={() => <ErrorPage type="pageNotFound" code={404} />}
              />
            </Switch>
          </LoggedOutPayment>
        )}
      />
      {/* ============= ADMIN ============= */}
      <RouteWrapper
        exact
        path={ROUTES.ADMIN_STATS}
        component={userIsSuperAdmin(AdminStatsPage)}
      />
      <RouteWrapper
        exact
        path={ROUTES.ADMIN_STATS_GENERIC}
        component={userIsSuperAdmin(AdminPrettyJson)}
      />
      <RouteWrapper
        path={`${ROUTES.ADMIN_STATS}/:type`}
        component={userIsSuperAdmin(AdminStatsPage)}
      />
      {/* ============= ADMIN V2 ============= */}
      <RouteWrapper
        path={ROUTES.ADMIN_V2}
        component={userIsSuperAdmin(() => (
          <AdminPageV2>
            <RouteWrapper
              path={ROUTES.ADMIN_V2_STATS_SCHOOLS}
              component={SchoolsStatsPage}
            />
            <RouteWrapper
              path={ROUTES.ADMIN_V2_STATS_EDUCATORS}
              component={EducatorsStatsPage}
            />
            <RouteWrapper
              path={ROUTES.ADMIN_V2_STATS_GRAPHS}
              component={GraphsStatsPage}
            />
          </AdminPageV2>
        ))}
      />
      {/* ============= NOT FOUND ============= */}
      <RouteWrapper
        component={() => <ErrorPage type="pageNotFound" code={404} />}
      />
    </Switch>
  </App>
);

export default Routes;
